import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import { scheduleApi } from '../lib/api';
import type { Schedule } from '@dutchinvoice/types';
import { auth } from '../config/firebase';
import { useInvoiceStore } from './invoiceStore';

// Define the types directly in this file
export enum ScheduleLoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  SAVING = 'saving',
  ERROR = 'error'
}

export interface ScheduleError {
  code: string;
  message: string;
  details?: unknown;
}

interface ScheduleState {
  schedules: Schedule[];
  loadingState: ScheduleLoadingState;
  error: ScheduleError | null;
  schedulesById: Record<string, Schedule | null>;

  // Actions
  fetchSchedules: (activeOnly?: boolean) => Promise<Schedule[]>;
  fetchScheduleForInvoice: (invoiceId: string) => Promise<Schedule | null>;
  createSchedule: (schedule: Omit<Schedule, 'id'>) => Promise<Schedule>;
  updateSchedule: (id: string, schedule: Partial<Schedule>) => Promise<Schedule>;
  deleteSchedule: (id: string) => Promise<void>;
  updateScheduleInCache: (invoiceId: string, schedule: Schedule | null) => void;
  resetError: () => void;
}

// Helper function to check if user is authenticated
const isUserAuthenticated = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(!!user);
    });
  });
};

export const useScheduleStore = create<ScheduleState>()(
  persist(
    (set, get) => ({
      schedules: [],
      loadingState: ScheduleLoadingState.IDLE,
      error: null,
      schedulesById: {},

      fetchSchedules: async (activeOnly = false) => {
        try {
          // Check if user is authenticated before making API call
          const isAuthenticated = await isUserAuthenticated();
          if (!isAuthenticated) {
            console.warn('User not authenticated, skipping fetchSchedules');
            return [];
          }

          set({ loadingState: ScheduleLoadingState.LOADING });
          const schedules = await scheduleApi.getSchedules(activeOnly);
          set({ schedules, loadingState: ScheduleLoadingState.IDLE });
          return schedules;
        } catch (error) {
          console.error('Failed to fetch schedules:', error);
          set({
            loadingState: ScheduleLoadingState.ERROR,
            error: {
              code: 'fetch_schedules_error',
              message: 'Failed to fetch schedules',
              details: error
            }
          });
          return [];
        }
      },

      fetchScheduleForInvoice: async (invoiceId: string) => {
        try {
          // Check if user is authenticated before making API call
          const isAuthenticated = await isUserAuthenticated();
          if (!isAuthenticated) {
            console.warn('User not authenticated, skipping fetchScheduleForInvoice');
            return null;
          }

          // Check if we already have this schedule in cache
          const existingSchedule = get().schedulesById[invoiceId];
          if (existingSchedule) {
            return existingSchedule;
          }

          set({ loadingState: ScheduleLoadingState.LOADING });
          const schedule = await scheduleApi.getScheduleForInvoice(invoiceId);

          // Cache the schedule by invoice ID
          set(state => ({
            loadingState: ScheduleLoadingState.IDLE,
            schedulesById: {
              ...state.schedulesById,
              [invoiceId]: schedule
            }
          }));

          return schedule;
        } catch (error) {
          console.error(`Failed to fetch schedule for invoice ${invoiceId}:`, error);
          set({
            loadingState: ScheduleLoadingState.ERROR,
            error: {
              code: 'fetch_schedule_for_invoice_error',
              message: `Failed to fetch schedule for invoice ${invoiceId}`,
              details: error
            }
          });
          return null;
        }
      },

      createSchedule: async (scheduleData: Omit<Schedule, 'id'>) => {
        try {
          // Check if user is authenticated before making API call
          const isAuthenticated = await isUserAuthenticated();
          if (!isAuthenticated) {
            console.warn('User not authenticated, skipping createSchedule');
            throw new Error('User not authenticated');
          }

          set({ loadingState: ScheduleLoadingState.SAVING });

          // Generate a temporary ID for optimistic updates
          const tempId = nanoid();
          const newSchedule = { ...scheduleData, id: tempId };

          // Make the API call
          const createdSchedule = await scheduleApi.createSchedule(newSchedule);

          // Update schedules list and cache
          set(state => ({
            schedules: [...state.schedules, createdSchedule],
            schedulesById: {
              ...state.schedulesById,
              [createdSchedule.templateInvoiceId]: createdSchedule
            },
            loadingState: ScheduleLoadingState.IDLE
          }));

          return createdSchedule;
        } catch (error) {
          console.error('Failed to create schedule:', error);
          set({
            loadingState: ScheduleLoadingState.ERROR,
            error: {
              code: 'create_schedule_error',
              message: 'Failed to create schedule',
              details: error
            }
          });
          throw error;
        }
      },

      updateSchedule: async (id: string, scheduleData: Partial<Schedule>) => {
        try {
          // Check if user is authenticated before making API call
          const isAuthenticated = await isUserAuthenticated();
          if (!isAuthenticated) {
            console.warn('User not authenticated, skipping updateSchedule');
            throw new Error('User not authenticated');
          }

          set({ loadingState: ScheduleLoadingState.SAVING });

          // Get the current schedule from the API to ensure we have the latest data
          const currentSchedule = await scheduleApi.getSchedule(id);
          if (!currentSchedule) {
            throw new Error(`Schedule with ID ${id} not found`);
          }

          // Create updated schedule
          const updatedSchedule = { ...currentSchedule, ...scheduleData };

          // Make the API call
          const savedSchedule = await scheduleApi.updateSchedule(id, updatedSchedule);

          // Update schedules list and cache
          set(state => ({
            schedules: state.schedules.map(s =>
              s.id === id ? savedSchedule : s
            ),
            schedulesById: {
              ...state.schedulesById,
              [savedSchedule.templateInvoiceId]: savedSchedule
            },
            loadingState: ScheduleLoadingState.IDLE
          }));

          return savedSchedule;
        } catch (error) {
          console.error(`Failed to update schedule ${id}:`, error);
          set({
            loadingState: ScheduleLoadingState.ERROR,
            error: {
              code: 'update_schedule_error',
              message: `Failed to update schedule ${id}`,
              details: error
            }
          });
          throw error;
        }
      },

      deleteSchedule: async (id: string) => {
        try {
          // Check if user is authenticated before making API call
          const isAuthenticated = await isUserAuthenticated();
          if (!isAuthenticated) {
            console.warn('User not authenticated, skipping deleteSchedule');
            throw new Error('User not authenticated');
          }

          set({ loadingState: ScheduleLoadingState.SAVING });

          // Get the schedule before deleting to know which invoice it belongs to
          const scheduleToDelete = get().schedules.find(s => s.id === id);
          const invoiceId = scheduleToDelete?.templateInvoiceId;

          // Make the API call
          await scheduleApi.deleteSchedule(id);

          // Update schedules list and cache
          set(state => {
            const newState: Partial<ScheduleState> = {
              schedules: state.schedules.filter(s => s.id !== id),
              loadingState: ScheduleLoadingState.IDLE
            };

            // Remove from cache if we know the invoice ID
            if (invoiceId) {
              newState.schedulesById = { ...state.schedulesById };
              delete newState.schedulesById[invoiceId];
            }

            return newState as ScheduleState;
          });
        } catch (error) {
          console.error(`Failed to delete schedule ${id}:`, error);
          set({
            loadingState: ScheduleLoadingState.ERROR,
            error: {
              code: 'delete_schedule_error',
              message: `Failed to delete schedule ${id}`,
              details: error
            }
          });
          throw error;
        }
      },

      // Update schedule in cache without API call (for concept invoices)
      updateScheduleInCache: (invoiceId: string, schedule: Schedule | null) => {
        set(state => ({
          schedulesById: {
            ...state.schedulesById,
            [invoiceId]: schedule
          }
        }));
      },

      resetError: () => {
        set({ error: null });
      }
    }),
    {
      name: 'schedule-store',
      partialize: (state) => ({
        schedules: state.schedules,
        schedulesById: state.schedulesById
      }),
    }
  )
);

// Selectors
export const useSchedules = () => useScheduleStore((state) => state.schedules);
export const useScheduleLoadingState = () => useScheduleStore((state) => state.loadingState);
export const useScheduleError = () => useScheduleStore((state) => state.error);

// Selector for getting a schedule by invoice ID
export const useScheduleForInvoice = (invoiceId: string | undefined) => {
  const currentInvoice = useInvoiceStore(state => state.currentInvoice);
  const schedulesById = useScheduleStore(state => state.schedulesById);

  // If no invoice ID, return null
  if (!invoiceId) return null;

  // For the current invoice that's a concept with scheduleData
  if (
    currentInvoice.id === invoiceId &&
    currentInvoice.status === 'concept' &&
    currentInvoice.scheduleData
  ) {
    // Return the concept invoice's schedule data
    return currentInvoice.scheduleData;
  }

  // For regular invoices, return from cache
  return schedulesById[invoiceId] || null;
};
